import React from "react";
import { Link } from "react-router-dom";
import SafeIcon from "../../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const {
  FiUsers, FiActivity, FiClock, FiWifi, FiBarChart2, FiChevronRight,
  FiArrowRight,
} = FiIcons;

/**
 * Clinical Dashboard Overview summary. Fed by useClinicalOverviewStats
 * (independent query — see that hook's comments for why). Renders real
 * numbers for activePatients, alertTotals, and deviceUptimePct; shows a
 * clearly-labelled "Not yet available" / "Coming soon" state for
 * avgResponseTimeMinutes and population analytics rather than fabricating
 * either.
 */
const ClinicalOverviewCard = ({ stats, isLoading }) => {
  const tiles = [
    { key: "activePatients", label: "Active Patients", icon: FiUsers, value: stats.activePatients, cls: "text-slate-200" },
    { key: "alertTotals", label: "Alert Totals", icon: FiActivity, value: stats.alertTotals, cls: "text-amber-500" },
    {
      key: "deviceUptimePct",
      label: "Device Uptime",
      icon: FiWifi,
      value: stats.deviceUptimePct !== null ? `${stats.deviceUptimePct}%` : "—",
      cls: "text-emerald-500",
    },
    {
      key: "avgResponseTimeMinutes",
      label: "Avg Response Time",
      icon: FiClock,
      value: stats.avgResponseTimeMinutes !== null ? `${stats.avgResponseTimeMinutes} min` : "Not yet available",
      cls: "text-slate-500",
      muted: stats.avgResponseTimeMinutes === null,
    },
  ];

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-3xl p-4 lg:p-6 backdrop-blur-xl mb-4 lg:mb-6">
      <div className="flex items-center gap-2 mb-4">
        <SafeIcon icon={FiUsers} className="text-slate-400" />
        <h3 className="text-sm font-bold text-slate-200 uppercase tracking-widest">Hospital Summary</h3>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">Loading summary...</p>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {tiles.map((tile) => (
            <div key={tile.key} className="text-center">
              <div className="flex items-center justify-center gap-1.5">
                <SafeIcon icon={tile.icon} className={`text-sm ${tile.cls}`} />
                <p className={`text-xl lg:text-2xl font-black ${tile.muted ? "text-slate-500 text-sm lg:text-base" : tile.cls}`}>
                  {tile.value}
                </p>
              </div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{tile.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Population Analytics — placeholder, no pipeline exists yet */}
      <div className="rounded-2xl bg-slate-800/40 border border-slate-700/50 px-4 py-3 mb-6 flex items-center gap-3">
        <SafeIcon icon={FiBarChart2} className="text-slate-500" />
        <div>
          <p className="text-sm font-semibold text-slate-300">Population Analytics</p>
          <p className="text-xs text-slate-500">
            Trends and cohort analytics will appear here once the analytics pipeline is connected.
          </p>
        </div>
      </div>

      {/* Shortcuts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Link
          to="/dashboard/triage"
          className="flex items-center justify-between rounded-2xl bg-blue-600/10 border border-blue-500/20 px-4 py-3 hover:bg-blue-600/20 transition-colors group"
        >
          <span className="text-sm font-bold text-blue-300 flex items-center gap-2">
            <SafeIcon icon={FiActivity} /> Go to Triage Centre
          </span>
          <SafeIcon icon={FiArrowRight} className="text-blue-400 group-hover:translate-x-1 transition-transform" />
        </Link>
        <Link
          to="/dashboard/roster"
          className="flex items-center justify-between rounded-2xl bg-slate-800/60 border border-slate-700/50 px-4 py-3 hover:bg-slate-800 transition-colors group"
        >
          <span className="text-sm font-bold text-slate-300 flex items-center gap-2">
            <SafeIcon icon={FiUsers} /> Go to Patient Roster
          </span>
          <SafeIcon icon={FiChevronRight} className="text-slate-400 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
};

export default ClinicalOverviewCard;